import React, { useState } from 'react';
import { Check, X, AlertCircle, Calendar, DollarSign, Filter, Search, ChevronDown, ChevronUp, FileText, ExternalLink, Image, XCircle, CheckCircle, Edit2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { formatCurrency, formatDate, getRegistrationFeeLabel } from '../../../utils/helpers';
import { Users } from 'lucide-react';

const ExpenseList = ({ expenses, games = [], players = [], tournaments = [], clubName, onEdit, onDelete, onApprove, onDeny, onSettleTournamentFee, isTreasurer, currentUser, showActions = true }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [actionModal, setActionModal] = useState({ isOpen: false, type: null, expense: null, note: '' });

    // Advanced Filters State
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [amountRange, setAmountRange] = useState({ min: '', max: '' });
    const [statusFilter, setStatusFilter] = useState('all');
    const [paidByFilter, setPaidByFilter] = useState('all');
    const [splitFilter, setSplitFilter] = useState('all');

    // Sorting State
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

    // Handle Sort
    const handleSort = (key) => {
        setSortConfig(current => {
            if (current.key === key) {
                return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
            }
            // Default desc for date/amount, asc for string
            const defaultDesc = ['date', 'amount', 'approvedAt'].includes(key);
            return { key, direction: defaultDesc ? 'desc' : 'asc' };
        });
    };

    // Filter Logic
    const filteredExpenses = expenses.filter(expense => {
        // Text Search
        const matchesSearch =
            expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            expense.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            expense.paidByAdminName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            expense.addedByPlayerName?.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;

        // Date Range
        const expenseDate = getExpenseDate(expense.date || expense.createdAt);
        if (dateRange.start) {
            const start = new Date(dateRange.start);
            start.setHours(0, 0, 0, 0);
            if (!expenseDate || expenseDate < start) return false;
        }
        if (dateRange.end) {
            const end = new Date(dateRange.end);
            end.setHours(23, 59, 59, 999);
            if (!expenseDate || expenseDate > end) return false;
        }

        // Amount Range
        if (amountRange.min !== '' && expense.amount < parseFloat(amountRange.min)) return false;
        if (amountRange.max !== '' && expense.amount > parseFloat(amountRange.max)) return false;

        // Status Filter
        if (statusFilter !== 'all' && expense.approvalStatus !== statusFilter) return false;

        // Paid By Filter
        if (paidByFilter !== 'all') {
            if (paidByFilter === 'club') {
                if (expense.paidBy !== 'club') return false;
            } else {
                if (expense.paidBy === 'club') return false; // Looking for 'member', so exclude 'club'
            }
        }

        // Split Filter
        if (splitFilter !== 'all') {
            const type = expense.splitType || (expense.gameId ? 'squad' : (expense.tournamentId ? 'tournament' : 'club')); // Infer if missing
            if (type !== splitFilter) return false;
        }

        return true;
    });

    // Sort Logic
    const sortedExpenses = [...filteredExpenses].sort((a, b) => {
        const { key, direction } = sortConfig;

        let comparison = 0;
        switch (key) {
            case 'date': {
                const dateA = getExpenseDate(a.date || a.createdAt) || new Date(0);
                const dateB = getExpenseDate(b.date || b.createdAt) || new Date(0);
                comparison = dateA - dateB;
                break;
            }
            case 'description':
                comparison = (a.description || '').localeCompare(b.description || '');
                break;
            case 'paidBy': {
                const nameA = a.paidBy === 'club' ? 'Club Account' : (a.paidByAdminName || a.addedByPlayerName || '');
                const nameB = b.paidBy === 'club' ? 'Club Account' : (b.paidByAdminName || b.addedByPlayerName || '');
                comparison = nameA.localeCompare(nameB);
                break;
            }
            case 'split': {
                const typeA = a.splitType || (a.gameId ? 'squad' : (a.tournamentId ? 'tournament' : 'club'));
                const typeB = b.splitType || (b.gameId ? 'squad' : (b.tournamentId ? 'tournament' : 'club'));
                comparison = typeA.localeCompare(typeB);
                break;
            }
            case 'amount':
                comparison = (a.amount || 0) - (b.amount || 0);
                break;
            case 'status':
                comparison = (a.approvalStatus || '').localeCompare(b.approvalStatus || '');
                break;
            case 'approvedAt': {
                const dateA = getExpenseDate(a.approvedAt) || new Date(0);
                const dateB = getExpenseDate(b.approvedAt) || new Date(0);
                comparison = dateA - dateB;
                break;
            }
            default:
                comparison = 0;
        }

        return direction === 'asc' ? comparison : -comparison;
    });

    // Helper for Sort Icon
    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) return <ArrowUpDown size={14} className="text-gray-400 ml-1" />;
        return sortConfig.direction === 'asc'
            ? <ArrowUp size={14} className="text-blue-600 ml-1" />
            : <ArrowDown size={14} className="text-blue-600 ml-1" />;
    };

    const handleActionClick = (expense, type) => {
        setActionModal({
            isOpen: true,
            type,
            expense,
            note: type === 'deny' ? '' : '' // Default empty, can pre-fill if needed
        });
    };

    const confirmAction = () => {
        const { type, expense, note } = actionModal;
        if (type === 'approve') {
            onApprove(expense, note);
        } else if (type === 'deny') {
            onDeny(expense, note);
        }
        setActionModal({ isOpen: false, type: null, expense: null, note: '' });
    };

    function getExpenseDate(date) {
        if (!date) return null;
        // Handle Firestore Timestamp
        if (date.toDate && typeof date.toDate === 'function') {
            return date.toDate();
        }
        // Handle raw seconds object (sometimes happens with serialized data)
        if (date.seconds) {
            return new Date(date.seconds * 1000);
        }
        // Handle string or number
        const parsed = new Date(date);
        return isNaN(parsed.getTime()) ? null : parsed;
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
            <div className="p-4 border-b border-gray-200 space-y-4">
                <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
                    <h3 className="font-bold text-gray-700">All Expenses ({filteredExpenses.length})</h3>

                    {/* Clear Filters Button */}
                    {(searchTerm || dateRange.start || dateRange.end || amountRange.min || amountRange.max || statusFilter !== 'all' || paidByFilter !== 'all' || splitFilter !== 'all') && (
                        <button
                            onClick={() => {
                                setSearchTerm('');
                                setDateRange({ start: '', end: '' });
                                setAmountRange({ min: '', max: '' });
                                setStatusFilter('all');
                                setPaidByFilter('all');
                                setSplitFilter('all');
                            }}
                            className="text-red-600 text-sm hover:underline flex items-center gap-1"
                        >
                            <X size={14} /> Clear Filters
                        </button>
                    )}
                </div>

                <div className="flex flex-wrap gap-4 items-center">
                    {/* Search */}
                    <div className="relative w-full md:w-64">
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                        />
                        <Filter className="absolute left-3 top-2.5 text-gray-400" size={16} />
                    </div>

                    {/* Date Range */}
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                            className="p-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="Start"
                        />
                        <span className="text-gray-400">-</span>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                            className="p-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="End"
                        />
                    </div>

                    {/* Amount Range */}
                    <div className="flex items-center gap-2">
                        <div className="relative w-20">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                            <input
                                type="number"
                                placeholder="Min"
                                value={amountRange.min}
                                onChange={(e) => setAmountRange({ ...amountRange, min: e.target.value })}
                                className="w-full pl-5 pr-2 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500"
                            />
                        </div>
                        <span className="text-gray-400">-</span>
                        <div className="relative w-20">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                            <input
                                type="number"
                                placeholder="Max"
                                value={amountRange.max}
                                onChange={(e) => setAmountRange({ ...amountRange, max: e.target.value })}
                                className="w-full pl-5 pr-2 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500"
                            />
                        </div>
                    </div>

                    {/* Dropdowns */}
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="p-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500"
                    >
                        <option value="all">All Statuses</option>
                        <option value="approved">Approved</option>
                        <option value="pending">Pending</option>
                        <option value="denied">Denied</option>
                    </select>

                    <select
                        value={paidByFilter}
                        onChange={(e) => setPaidByFilter(e.target.value)}
                        className="p-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500"
                    >
                        <option value="all">Paid By Any</option>
                        <option value="club">Club</option>
                        <option value="member">Member</option>
                    </select>

                    <select
                        value={splitFilter}
                        onChange={(e) => setSplitFilter(e.target.value)}
                        className="p-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500"
                    >
                        <option value="all">Any Split</option>
                        <option value="club">Club</option>
                        <option value="squad">Squad</option>
                        <option value="tournament">Tournament</option>
                    </select>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50 text-gray-600 text-sm border-b border-gray-200">
                            <th className="p-4 font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('date')}>
                                <div className="flex items-center">Date <SortIcon columnKey="date" /></div>
                            </th>
                            <th className="p-4 font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('description')}>
                                <div className="flex items-center">Description <SortIcon columnKey="description" /></div>
                            </th>
                            <th className="p-4 font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('paidBy')}>
                                <div className="flex items-center">Paid By <SortIcon columnKey="paidBy" /></div>
                            </th>
                            <th className="p-4 font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('split')}>
                                <div className="flex items-center">Split <SortIcon columnKey="split" /></div>
                            </th>
                            <th className="p-4 font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('amount')}>
                                <div className="flex items-center">Amount <SortIcon columnKey="amount" /></div>
                            </th>
                            <th className="p-4 font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('status')}>
                                <div className="flex items-center">Status <SortIcon columnKey="status" /></div>
                            </th>
                            <th className="p-4 font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('approvedAt')}>
                                <div className="flex items-center">Reviewed On <SortIcon columnKey="approvedAt" /></div>
                            </th>
                            <th className="p-4 font-semibold text-center">Receipt</th>
                            {showActions && <th className="p-4 font-semibold text-right">Actions</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {sortedExpenses.length > 0 ? (
                            sortedExpenses.map((expense) => {
                                const expenseDate = getExpenseDate(expense.date || expense.createdAt);
                                const approvedDate = getExpenseDate(expense.approvedAt);
                                // Check if current user is the owner or beneficiary
                                const isOwner = currentUser && (
                                    expense.createdBy === currentUser.email ||
                                    expense.addedByPlayerId === currentUser.uid
                                );

                                return (
                                    <tr key={expense.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="p-4 text-sm text-gray-600">
                                            {expenseDate
                                                ? formatDate(expenseDate)
                                                : <span className="text-gray-400 italic">No Date</span>}
                                        </td>
                                        <td className="p-4">
                                            <div className="font-medium text-gray-900">
                                                {(expense.description || expense.type) === 'Initial Donation' 
                                                    ? getRegistrationFeeLabel(clubName) 
                                                    : (expense.description || expense.type)}
                                            </div>
                                            {expense.gameId && (() => {
                                                const game = games?.find(g => g.id === expense.gameId);
                                                return (
                                                    <div className="text-xs text-gray-500">
                                                        Game Expense {game ? `- vs ${game.opponent} on ${formatDate(game.date)}` : ''}
                                                    </div>
                                                );
                                            })()}
                                            {expense.approvalNote && (
                                                <div className="mt-1 text-xs text-green-600 flex items-center gap-1">
                                                    <span className="font-semibold">Note:</span>
                                                    {expense.approvalNote}
                                                </div>
                                            )}
                                            {expense.rejectionReason && (
                                                <div className="mt-1 text-xs text-red-600 flex items-center gap-1">
                                                    <span className="font-semibold">Reason:</span>
                                                    {expense.rejectionReason}
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4 text-sm">
                                            {expense.paidBy === 'club' ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                    Club Account
                                                </span>
                                            ) : (
                                                <span className="font-medium text-blue-600">
                                                    {(() => {
                                                        const isInitial = expense.isInitialDonation || expense.expenseType === 'Initial Donation' || expense.description === 'Initial Donation';
                                                        const pId = isInitial 
                                                            ? (expense.splitAmong?.[0] || expense.playerId) 
                                                            : (expense.playerId || expense.addedByPlayerId || expense.userId || expense.submittedBy || expense.splitAmong?.[0]);
                                                        const p = players?.find(player => player.id === pId);
                                                        return p?.name || (isInitial ? expense.playerName : null) || expense.playerName || expense.paidByAdminName || expense.addedByPlayerName || 'Unknown Player';
                                                    })()}
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4 text-sm">
                                            {(() => {
                                                const type = expense.splitType || (expense.gameId ? 'squad' : (expense.tournamentId ? 'tournament' : 'club'));
                                                const tournamentName = type === 'tournament'
                                                    ? (expense.tournamentName || tournaments.find(t => t.id === expense.tournamentId)?.name || null)
                                                    : null;
                                                return (
                                                    <div>
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${type === 'squad' ? 'bg-green-50 text-green-700' :
                                                            type === 'tournament' ? 'bg-purple-50 text-purple-700' :
                                                                'bg-gray-100 text-gray-600'
                                                            }`}>
                                                            {type === 'squad' ? 'Squad' :
                                                                type === 'tournament' ? 'Tournament' : 'Club'}
                                                        </span>
                                                        {tournamentName && (
                                                            <div className="text-xs text-gray-500 mt-0.5 max-w-[140px] truncate" title={tournamentName}>
                                                                {tournamentName}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="p-4 font-bold text-gray-900">
                                            {formatCurrency(expense.amount)}
                                        </td>
                                        <td className="p-4">
                                            {expense.approvalStatus === 'pending' ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    <AlertCircle size={12} /> Pending
                                                </span>
                                            ) : expense.approvalStatus === 'approved' ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    <CheckCircle size={12} /> Approved
                                                </span>
                                            ) : expense.approvalStatus === 'denied' ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    <XCircle size={12} /> Denied
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 text-xs">-</span>
                                            )}
                                        </td>
                                        <td className="p-4 text-sm text-gray-600">
                                            {(expense.approvedAt || expense.deniedAt) ? (
                                                <div>
                                                    <div>{formatDate(expense.approvedAt || expense.deniedAt)}</div>
                                                    {(expense.approvedByName || expense.deniedByName) && (
                                                        <div className="text-xs text-gray-500 mt-0.5">
                                                            by {expense.approvedByName || expense.deniedByName}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 italic">-</span>
                                            )}
                                        </td>
                                        <td className="p-4 text-center">
                                            {expense.receipt || expense.receiptURL ? (
                                                <button
                                                    onClick={() => handleActionClick(expense, 'view_receipt')}
                                                    className="inline-flex items-center justify-center p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="View Receipt"
                                                >
                                                    <FileText size={18} />
                                                </button>
                                            ) : (
                                                <span className="text-gray-300">-</span>
                                            )}
                                        </td>
                                        {showActions && (
                                            <td className="p-4 text-right">
                                                <div className="flex justify-end gap-2">
                                                    {/* Only show Approve/Deny for pending if treasurer AND NOT OWNER */}
                                                    {isTreasurer && expense.approvalStatus === 'pending' && !isOwner && (
                                                        <>
                                                            <button
                                                                onClick={() => handleActionClick(expense, 'approve')}
                                                                className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                                                                title="Approve"
                                                            >
                                                                <CheckCircle size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleActionClick(expense, 'deny')}
                                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                                                title="Deny"
                                                            >
                                                                <XCircle size={16} />
                                                            </button>
                                                        </>
                                                    )}

                                                    {/* Show disabled/greyed out buttons for owner to indicate they exist but are locked */}
                                                    {isTreasurer && expense.approvalStatus === 'pending' && isOwner && (
                                                        <>
                                                            <button
                                                                disabled
                                                                className="p-1.5 text-gray-300 cursor-not-allowed rounded"
                                                                title="You cannot approve your own expense"
                                                            >
                                                                <CheckCircle size={16} />
                                                            </button>
                                                            <button
                                                                disabled
                                                                className="p-1.5 text-gray-300 cursor-not-allowed rounded"
                                                                title="You cannot deny your own expense"
                                                            >
                                                                <XCircle size={16} />
                                                            </button>
                                                        </>
                                                    )}

                                                    {/* Settle - only for Tournament Fees */}
                                                    {onSettleTournamentFee && expense.isTournamentFee && expense.approvalStatus === 'approved' && !expense.settled && (
                                                        <button
                                                            onClick={() => onSettleTournamentFee(expense)}
                                                            className="p-1.5 text-purple-600 hover:bg-purple-50 hover:text-purple-800 rounded transition-colors"
                                                            title="Split Among Players"
                                                        >
                                                            <Users size={16} />
                                                        </button>
                                                    )}

                                                    <button
                                                        onClick={() => onEdit && onEdit(expense)}
                                                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={showActions ? 9 : 8} className="p-8 text-center text-gray-500">
                                    No expenses found matching filter.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Action Modal */}
            {actionModal.isOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className={`bg-white rounded-xl w-full shadow-xl ${actionModal.type === 'view_receipt' ? 'max-w-3xl' : 'max-w-md'} p-6`}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold">
                                {actionModal.type === 'approve' ? 'Approve Expense' :
                                    actionModal.type === 'deny' ? 'Deny Expense' :
                                        actionModal.type === 'view_receipt' ? 'View Receipt' : ''}
                            </h3>
                            {actionModal.type === 'view_receipt' && (
                                <button
                                    onClick={() => setActionModal({ isOpen: false, type: null, expense: null, note: '' })}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XCircle size={24} />
                                </button>
                            )}
                        </div>

                        {actionModal.type === 'view_receipt' ? (
                            <div className="flex flex-col items-center">
                                <div className="w-full bg-gray-100 rounded-lg p-4 mb-4 flex justify-center overflow-hidden min-h-[300px] max-h-[70vh]">
                                    {(function () {
                                        const url = actionModal.expense?.receipt || actionModal.expense?.receiptURL;
                                        if (!url) return <p className="text-gray-500">No receipt image available.</p>;

                                        // Simple check for PDF in URL (Firebase URLs usually keep the name)
                                        const isPdf = url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('application/pdf');

                                        if (isPdf) {
                                            // Use Google Docs Viewer to force preview and prevent auto-download
                                            const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
                                            return (
                                                <div className="w-full h-[500px] flex flex-col items-center justify-center bg-gray-200 rounded">
                                                    <iframe
                                                        src={viewerUrl}
                                                        className="w-full h-full rounded border-none"
                                                        title="Receipt PDF Preview"
                                                    />
                                                </div>
                                            );
                                        }

                                        return (
                                            <div className="flex flex-col items-center gap-2 w-full">
                                                <img
                                                    src={url}
                                                    alt="Receipt"
                                                    className="max-w-full max-h-[60vh] object-contain"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.parentElement.innerHTML = `
                                                            <div class="flex flex-col items-center justify-center text-gray-500 p-8 text-center">
                                                                <svg class="w-12 h-12 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>
                                                                <p class="font-semibold">Preview Unavailable</p>
                                                                <p class="text-xs mt-1">This file type cannot be previewed directly.</p>
                                                            </div>
                                                        `;
                                                    }}
                                                />
                                            </div>
                                        );
                                    })()}
                                </div>

                                <div className="w-full flex justify-between items-center px-2">
                                    <a
                                        href={actionModal.expense?.receipt || actionModal.expense?.receiptURL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                        Open Original
                                    </a>

                                    <button
                                        onClick={() => setActionModal({ isOpen: false, type: null, expense: null, note: '' })}
                                        className="px-6 py-2 bg-gray-600 text-white font-medium hover:bg-gray-700 rounded-lg transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <p className="text-gray-600 mb-4 text-sm">
                                    {actionModal.type === 'approve'
                                        ? 'Add an optional note for this approval.'
                                        : 'Please provide a reason for denying this expense.'}
                                </p>

                                <textarea
                                    value={actionModal.note}
                                    onChange={(e) => setActionModal(prev => ({ ...prev, note: e.target.value }))}
                                    placeholder={actionModal.type === 'approve' ? "Approval note (optional)..." : "Reason for denial..."}
                                    className="w-full border rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 mb-4 h-32"
                                    autoFocus
                                />

                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => setActionModal({ isOpen: false, type: null, expense: null, note: '' })}
                                        className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmAction}
                                        disabled={actionModal.type === 'deny' && !actionModal.note.trim()}
                                        className={`px-4 py-2 text-white font-medium rounded-lg ${actionModal.type === 'approve'
                                            ? 'bg-green-600 hover:bg-green-700'
                                            : 'bg-red-600 hover:bg-red-700 disabled:opacity-50'
                                            }`}
                                    >
                                        Confirm {actionModal.type === 'approve' ? 'Approval' : 'Denial'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpenseList;
